import { getI18nInfo } from '@fox/extensions/src/use-i18n';
import { strIsNullOrEmpty } from '@fox/shared';
import moment, { Moment } from 'moment';

export const ISO_DATE_FORMAT = 'YYYY-MM-DD';

export const DATE_DISPLAY_FORMAT_DMY = 'DD-MM-YYYY';

export const DAY_OF_WEEK_MAP: { [key: number]: string } = {
	0: 'Sunday',
	1: 'Monday',
	2: 'Tuesday',
	3: 'Wednesday',
	4: 'Thursday',
	5: 'Friday',
	6: 'Saturday'
};

/** Formats a date as words in DMY order, e.g. "14 Aug 2025"
 * Accepts a Date object or a string (ISO or parseable by Date).
 */
export function formatDateAsWordsDMY(date: Date | string | Moment, locale?: string): string | null {
	if (!date || (typeof date === 'string' && strIsNullOrEmpty(date))) return '';
	const m = moment(date);
	if (!m.isValid()) return '';
	// Use current locale or fallback
	m.locale(locale ?? window.__i18n?.locale ?? 'en-au');
	return m.format('D MMM YYYY');
}

/** Takes a javascript date and will convert it to the ISO date format YYYY-MM-DD suitable to send to a server */
export function dateToIsoDateOnly(date?: Date | null): string | null {
	return date ? moment(date).format(ISO_DATE_FORMAT) : null;
}

export function momentToIsoDateOnly(date?: Moment | null): string | null {
	return date ? date.format(ISO_DATE_FORMAT) : null;
}

export function stringToDate(str?: null | string): Date | null {
	if (!str) return null;

	return new Date(str);
}

export function stringToMoment(str?: null | string): Moment | null {
	if (!str) return null;

	return moment(str);
}

export function formatTime(milliseconds: number) {
	const totalSeconds = Math.floor(milliseconds / 1000);
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

/** Convert seconds since the start of day to a date object.*/
export const secondsToDate = (seconds: number) => {
	return moment().utc().startOf('day').add(seconds, 'seconds').toDate();
};

/** Convert a date to seconds since the start of a day.*/
export const dateToSeconds = (date: Date) => {
	return moment(date).utc().diff(moment(date).utc().startOf('day'), 'seconds');
};

/** Will format a date to DD/MM/YYYY or MM/DD/YYYY or YYYY/MM/DD format as expected by the locale. */
export const formatDateLocale = (date: Date | string, locale: string) => {
	if (!date) return '';
	return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(date));
};

/** Will format a date to a human friendly format with the month as a word, e.g. Tue, 7 Apr 2026, 12:46 pm */
export const formatDateTimeForDisplay = (dateStr: string) => {
	const { locale } = getI18nInfo();
	return new Date(dateStr).toLocaleString(locale, {
		weekday: 'short',
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit'
	});
};
